import React, { useState, useEffect, useRef } from 'react';
import { 
  Phone, 
  MapPin, 
  Clock, 
  Instagram, 
  Facebook,
  MessageCircle,
  Star,
  Flame,
  Sparkles,
  ChevronRight,
  ExternalLink,
  Award,
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  X,
  User,
  Search,
  CheckCircle,
  Home,
  Store,
  MapPinned,
  FileText,
  Loader2,
  ArrowLeft,
  ArrowRight,
  PartyPopper,
  Gift,
  LogOut,
  Eye,
  Check
} from 'lucide-react';
import { toast } from 'sonner';
import { FroggyAssistant, SmartRecommendations, UpsellModal, LoyaltyProgress } from '../components/menu/SmartSalesSystem';
import { parseMarkdown } from '../lib/markdown';
import InstallAppPrompt from '../components/menu/InstallAppPrompt';
import PromoCarousel from '../components/menu/PromoCarousel';
import CategoryScroller from '../components/menu/CategoryScroller';
import ProductCard from '../components/menu/ProductCard';
import MenuHeader from '../components/menu/MenuHeader';
import SearchBar from '../components/menu/SearchBar';
import PromoPopup from '../components/menu/PromoPopup';
import FroggyEliteAnimated from '../components/menu/FroggyEliteAnimated';

// TikTok icon component
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
  </svg>
);

const MenuPublic = () => {
  const [businessInfo, setBusinessInfo] = useState(null);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [carouselItems, setCarouselItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLine, setSelectedLine] = useState('all'); // all, fria, caliente, bebidas
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(''); // Search functionality

  // Product Detail Modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productExtras, setProductExtras] = useState([]); // Extras disponibles para el producto
  const [selectedExtras, setSelectedExtras] = useState([]); // Extras seleccionados por el cliente

  // Product Notes Modal (for adding notes before adding to cart)
  const [showProductNotesModal, setShowProductNotesModal] = useState(false);
  const [productForNotes, setProductForNotes] = useState(null);
  const [tempProductNotes, setTempProductNotes] = useState('');

  // Install App Prompt
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);

  // Cart State
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  
  // Checkout State
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(1); // 1: Customer, 2: Delivery, 3: Confirm, 4: Success
  const [searchingCustomer, setSearchingCustomer] = useState(false);
  const [foundCustomer, setFoundCustomer] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [autoSearchDone, setAutoSearchDone] = useState(false); // Track if auto-search was done
  
  // Smart Sales System State
  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  
  // Promo Popup State
  const [activePromotion, setActivePromotion] = useState(null);
  const [allActivePromotions, setAllActivePromotions] = useState([]);
  const [showPromoPopup, setShowPromoPopup] = useState(false);
  
  // Lead Capture State
  const [showLeadCapture, setShowLeadCapture] = useState(false);
  const [leadPhone, setLeadPhone] = useState('');
  const [leadSubmitting, setLeadSubmitting] = useState(false);
  const [viewedPromoWithoutAction, setViewedPromoWithoutAction] = useState(null);
  
  // Customer Form
  const [customerForm, setCustomerForm] = useState({
    phone: '',
    name: '',
    orderType: 'delivery', // delivery or pickup
    address: '',
    colony: '',
    references: '',
    notes: ''
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchLandingData();
    fetchActivePromotion();
    
    // Show install prompt after 3 seconds if not already installed and not dismissed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const isDismissed = localStorage.getItem('installPromptDismissed');
    
    if (!isStandalone && !isDismissed) {
      const timer = setTimeout(() => {
        setShowInstallPrompt(true);
      }, 15000); // 15 seconds delay to not interrupt user browsing
      return () => clearTimeout(timer);
    }
  }, []);

  // Fetch all active promotions and select one to show (rotation)
  const fetchActivePromotion = async () => {
    try {
      // Fetch ALL active promotions
      const res = await fetch(`${BACKEND_URL}/api/promotions/active/all`);
      if (res.ok) {
        const promos = await res.json();
        if (promos && promos.length > 0) {
          // Save all promotions for the offers section
          setAllActivePromotions(promos);
          
          // Rotation logic: pick a different promo each visit
          const lastShownPromoIndex = parseInt(localStorage.getItem('last_promo_index') || '-1');
          const nextIndex = (lastShownPromoIndex + 1) % promos.length;
          const promo = promos[nextIndex];
          
          // Check frequency rules for the selected promo
          const lastShownKey = `promo_${promo.id}_lastShown`;
          const lastShown = localStorage.getItem(lastShownKey);
          const now = new Date().getTime();
          
          let shouldShow = false;
          
          if (promo.frequency === 'every_visit') {
            shouldShow = true;
          } else if (promo.frequency === 'once_per_day') {
            if (!lastShown || (now - parseInt(lastShown)) > 24 * 60 * 60 * 1000) {
              shouldShow = true;
            }
          } else if (promo.frequency === 'first_time_only') {
            if (!lastShown) {
              shouldShow = true;
            }
          } else {
            // Default: always show for rotation
            shouldShow = true;
          }
          
          if (shouldShow) {
            setActivePromotion(promo);
            setTimeout(() => {
              setShowPromoPopup(true);
              // Record view
              fetch(`${BACKEND_URL}/api/promotions/${promo.id}/view`, { method: 'POST' }).catch(() => {});
              // Save rotation index
              localStorage.setItem('last_promo_index', nextIndex.toString());
              // Save tracking info (only for non-every_visit)
              if (promo.frequency !== 'every_visit') {
                localStorage.setItem(lastShownKey, now.toString());
              }
            }, 1500);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  // Auto-search customer when phone has 10 digits
  useEffect(() => {
    const phone = customerForm.phone.replace(/\D/g, '');
    if (phone.length >= 10 && !autoSearchDone && !searchingCustomer) {
      // Debounce the search
      const timer = setTimeout(() => {
        searchCustomerAuto(phone);
      }, 500);
      return () => clearTimeout(timer);
    }
    // Reset auto-search flag when phone changes significantly
    if (phone.length < 10) {
      setAutoSearchDone(false);
      setFoundCustomer(null);
      setIsNewCustomer(false);
    }
  }, [customerForm.phone]);

  const fetchLandingData = async () => {
    try {
      const [res, carouselRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/menu-public/landing/mr-frogs`),
        fetch(`${BACKEND_URL}/api/promotions/carousel-items`)
      ]);
      if (res.ok) {
        const data = await res.json();
        setBusinessInfo(data.business);
        setCategories(data.categories);
        setProducts(data.products);
        setPromotions(data.promotions);
      }
      if (carouselRes.ok) {
        const carousel = await carouselRes.json();
        setCarouselItems(carousel.items || []);
      }
    } catch (error) {
      console.error('Error loading menu:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Cargar extras cuando se selecciona un producto
  useEffect(() => {
    if (selectedProduct) {
      setSelectedExtras([]); // Reset extras seleccionados
      // Fetch extras disponibles para este producto
      fetch(`${BACKEND_URL}/api/extras/public/products/${selectedProduct.product_id}/extras`)
        .then(res => res.ok ? res.json() : [])
        .then(data => setProductExtras(data || []))
        .catch(() => setProductExtras([]));
    } else {
      setProductExtras([]);
      setSelectedExtras([]);
    }
  }, [selectedProduct]);

  // Calcular total con extras
  const calculateTotalWithExtras = () => {
    const basePrice = selectedProduct?.price || 0;
    const extrasTotal = selectedExtras.reduce((sum, extra) => sum + extra.price, 0);
    return basePrice + extrasTotal;
  };

  // Handle install prompt close
  const handleInstallPromptClose = () => {
    setShowInstallPrompt(false);
    localStorage.setItem('installPromptDismissed', 'true');
  };

  // Cart Functions
  const addToCart = (product, notes = '') => {
    setCart(prev => {
      // Si tiene extras o notas, siempre agregar como item nuevo (no combinar)
      const hasExtras = product.selectedExtras && product.selectedExtras.length > 0;
      
      if (notes || hasExtras) {
        return [...prev, {
          product_id: product.product_id || product.id,
          name: product.name,
          price: product.totalPrice || product.price, // Usar precio total si tiene extras
          basePrice: product.price, // Precio base sin extras
          quantity: 1,
          image_url: product.image_url,
          notes: notes,
          is_promo_item: product.is_promo_item,
          promo_id: product.promo_id,
          promo_name: product.promo_name,
          original_price: product.original_price,
          selectedExtras: product.selectedExtras || [],
          extrasTotal: product.extrasTotal || 0
        }];
      }
      
      // No notes ni extras - combine with existing item without notes/extras
      const existing = prev.find(item => 
        item.product_id === (product.product_id || product.id) && 
        !item.notes && 
        (!item.selectedExtras || item.selectedExtras.length === 0)
      );
      if (existing) {
        return prev.map(item => 
          item.product_id === (product.product_id || product.id) && !item.notes && (!item.selectedExtras || item.selectedExtras.length === 0)
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id || product.id,
        name: product.name,
        price: product.price,
        basePrice: product.price,
        quantity: 1,
        image_url: product.image_url,
        notes: '',
        is_promo_item: product.is_promo_item,
        promo_id: product.promo_id,
        promo_name: product.promo_name,
        original_price: product.original_price,
        selectedExtras: [],
        extrasTotal: 0
      }];
    });
    
    // Mostrar toast con extras si los hay
    const extrasCount = product.selectedExtras?.length || 0;
    if (extrasCount > 0) {
      toast.success(`${product.name} + ${extrasCount} extra${extrasCount > 1 ? 's' : ''} agregado al carrito`);
    } else {
      toast.success(`${product.name} agregado al carrito`);
    }
  };

  // Open notes modal before adding to cart
  const openProductNotesModal = (product) => {
    setProductForNotes(product);
    setTempProductNotes('');
    setShowProductNotesModal(true);
  };

  // Add product with notes from modal
  const addProductWithNotes = () => {
    if (productForNotes) {
      addToCart(productForNotes, tempProductNotes);
      setShowProductNotesModal(false);
      setProductForNotes(null);
      setTempProductNotes('');
    }
  };

  // Update notes for an item in cart
  const updateItemNotes = (index, notes) => {
    setCart(prev => prev.map((item, i) => 
      i === index ? { ...item, notes } : item
    ));
  };

  const updateQuantity = (index, delta) => {
    setCart(prev => {
      return prev.map((item, i) => {
        if (i === index) {
          const newQty = item.quantity + delta;
          if (newQty <= 0) return null;
          return { ...item, quantity: newQty };
        }
        return item;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (index) => {
    setCart(prev => prev.filter((_, i) => i !== index));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  // Handle add to cart from promo popup
  const handlePromoAddToCart = (promo) => {
    // Find the product if linked, otherwise create a promo item
    if (promo.product_id) {
      const product = products.find(p => p.id === promo.product_id || p.product_id === promo.product_id);
      if (product) {
        addToCart({
          ...product,
          price: promo.promo_price, // Use promo price
          is_promo_item: true,
          promo_id: promo.id,  // Track which promo was used
          promo_name: promo.title,
          original_price: promo.original_price
        });
      } else {
        // Product not found, create from promo data
        addToCart({
          id: promo.product_id || `promo_${promo.id}`,
          name: promo.product_name || promo.title,
          price: promo.promo_price,
          image_url: promo.image_url,
          is_promo_item: true,
          promo_id: promo.id,  // Track which promo was used
          promo_name: promo.title,
          original_price: promo.original_price
        });
      }
    } else {
      // No linked product, create from promo data
      addToCart({
        id: `promo_${promo.id}`,
        name: promo.title,
        price: promo.promo_price,
        image_url: promo.image_url,
        is_promo_item: true,
        promo_id: promo.id,  // Track which promo was used
        promo_name: promo.title,
        original_price: promo.original_price
      });
    }
    
    // Record click for analytics
    fetch(`${BACKEND_URL}/api/promotions/${promo.id}/click`, { method: 'POST' }).catch(() => {});
    
    toast.success('¡Promoción agregada al carrito!');
  };

  // Customer Search (manual - with button)
  const searchCustomer = async () => {
    if (!customerForm.phone || customerForm.phone.length < 10) {
      toast.error('Ingresa un número de teléfono válido');
      return;
    }
    
    setSearchingCustomer(true);
    try {
      const phone = customerForm.phone.replace(/\D/g, '');
      const res = await fetch(`${BACKEND_URL}/api/menu-public/customer/search/${phone}`);
      const data = await res.json();
      
      if (data.found) {
        setFoundCustomer(data.customer);
        setCustomerForm(prev => ({
          ...prev,
          name: data.customer.name,
          address: data.customer.address || '',
          colony: data.customer.colony || ''
        }));
        setIsNewCustomer(false);
        setAutoSearchDone(true);
        toast.success(`¡Bienvenido de vuelta, ${data.customer.name}!`);
      } else {
        setFoundCustomer(null);
        setIsNewCustomer(true);
        setAutoSearchDone(true);
        toast.info('No encontramos tu número. Ingresa tus datos.');
      }
    } catch (error) {
      console.error('Error searching customer:', error);
      toast.error('Error al buscar cliente');
    } finally {
      setSearchingCustomer(false);
    }
  };

  // Auto-search customer (triggered automatically when phone is complete)
  const searchCustomerAuto = async (phone) => {
    if (autoSearchDone) return;
    
    setSearchingCustomer(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/menu-public/customer/search/${phone}`);
      const data = await res.json();
      
      if (data.found) {
        setFoundCustomer(data.customer);
        setCustomerForm(prev => ({
          ...prev,
          name: data.customer.name,
          address: data.customer.address || '',
          colony: data.customer.colony || ''
        }));
        setIsNewCustomer(false);
        toast.success(`¡Bienvenido de vuelta, ${data.customer.name}!`);
      } else {
        setFoundCustomer(null);
        setIsNewCustomer(true);
        // No toast for auto-search when not found - less intrusive
      }
      setAutoSearchDone(true);
    } catch (error) {
      console.error('Error auto-searching customer:', error);
    } finally {
      setSearchingCustomer(false);
    }
  };

  // Submit Order
  const submitOrder = async () => {
    if (!customerForm.name || !customerForm.phone) {
      toast.error('Completa tu nombre y teléfono');
      return;
    }
    
    if (customerForm.orderType === 'delivery' && !customerForm.address) {
      toast.error('Ingresa tu dirección de entrega');
      return;
    }
    
    setSubmittingOrder(true);
    try {
      const orderData = {
        customer_phone: customerForm.phone,
        customer_name: customerForm.name,
        customer_id: foundCustomer?.customer_id || null,
        order_type: customerForm.orderType,
        delivery_address: customerForm.address,
        delivery_colony: customerForm.colony,
        delivery_references: customerForm.references,
        items: cart,
        notes: customerForm.notes,
        subtotal: cartTotal,
        total: cartTotal
      };
      
      const res = await fetch(`${BACKEND_URL}/api/menu-public/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData)
      });
      
      const data = await res.json();
      
      if (data.success) {
        setOrderResult(data);
        setCheckoutStep(4);
        setCart([]);
        toast.success('¡Pedido enviado exitosamente!');
      } else {
        toast.error(data.detail || 'Error al crear pedido');
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Error al enviar pedido');
    } finally {
      setSubmittingOrder(false);
    }
  };

  // Reset checkout
  const resetCheckout = () => {
    setShowCheckout(false);
    setCheckoutStep(1);
    setFoundCustomer(null);
    setIsNewCustomer(false);
    setCustomerForm({
      phone: '',
      name: '',
      orderType: 'delivery',
      address: '',
      colony: '',
      references: '',
      notes: ''
    });
    setOrderResult(null);
  };

  // Define which categories belong to which line
  // Get line for a category - NOW USES kitchen_station from POS
  const getCategoryLine = (category) => {
    // If category object is passed, use its kitchen_station
    if (typeof category === 'object' && category !== null) {
      const station = category.kitchen_station || 'caliente';
      if (station === 'fria') return 'fria';
      if (station === 'caliente') return 'caliente';
      if (station === 'bebidas' || station === 'barra') return 'bebidas';
      return 'caliente';
    }
    
    // Fallback: if string (category name) is passed, try to find category
    const name = (category || '').toLowerCase();
    const cat = categories.find(c => c.name?.toLowerCase() === name);
    if (cat?.kitchen_station) {
      const station = cat.kitchen_station;
      if (station === 'fria') return 'fria';
      if (station === 'caliente') return 'caliente';
      if (station === 'bebidas' || station === 'barra') return 'bebidas';
    }
    
    // Ultimate fallback by name patterns
    if (name.includes('bebida') || name.includes('postre') || name.includes('refresco')) return 'bebidas';
    if (name.includes('ceviche') || name.includes('coctel') || name.includes('aguachile') || name.includes('tostada')) return 'fria';
    return 'caliente';
  };

  // Categories to hide from menu (generic/redundant categories)
  const hiddenCategories = [
    'cocina', 'cocina caliente', 'cocina fria', 'cocina fría', 
    'mariscos', 'calientes', 'fríos', 'barra', 'promociones', 'productos especiales'
  ];

  // Get categories filtered (excluding hidden ones)
  const visibleCategories = categories.filter(cat => 
    !hiddenCategories.includes(cat.name?.toLowerCase()?.trim())
  );

  // Filter products by category and search query
  const filteredProducts = (() => {
    let filtered = products;
    
    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.name?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query) ||
        p.category?.toLowerCase().includes(query)
      );
    }
    
    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category_id === selectedCategory);
    }
    
    return filtered;
  })();

  // Get cart quantity for a product (sum all items with same product_id)
  const getCartQuantity = (productId) => {
    return cart.filter(c => c.product_id === productId).reduce((sum, item) => sum + item.quantity, 0);
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat?.name || '';
  };

  // Handle promo popup close - show lead capture if they didn't add to cart
  const handlePromoPopupClose = () => {
    setShowPromoPopup(false);
    // If they closed without adding to cart, show lead capture after 2 seconds
    if (activePromotion) {
      setViewedPromoWithoutAction(activePromotion);
      setTimeout(() => {
        setShowLeadCapture(true);
      }, 2000);
    }
  };

  // Submit lead phone
  const handleLeadSubmit = async () => {
    if (!leadPhone || leadPhone.length < 10) {
      toast.error('Ingresa un número de teléfono válido');
      return;
    }
    
    setLeadSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/promotions/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone: leadPhone,
          promo_id: viewedPromoWithoutAction?.promo_id,
          promo_title: viewedPromoWithoutAction?.title,
          source: 'promo_popup'
        })
      });
      
      if (res.ok) {
        toast.success('¡Te avisaremos de ofertas especiales!');
        setShowLeadCapture(false);
        setLeadPhone('');
        localStorage.setItem('leadCaptured', 'true');
      }
    } catch (error) {
      console.error('Error saving lead:', error);
    } finally {
      setLeadSubmitting(false);
    }
  };

  // Record WhatsApp click
  const recordWhatsAppClick = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/promotions/leads/whatsapp-click`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          promo_id: activePromotion?.promo_id || null
        })
      });
    } catch (error) {
      console.error('Error recording click:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white/70">Cargando menú...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Promo Popup */}
      {showPromoPopup && activePromotion && (
        <PromoPopup
          promotion={activePromotion}
          onClose={handlePromoPopupClose}
          onAddToCart={handlePromoAddToCart}
        />
      )}

      {/* Lead Capture Popup - Soft Ask */}
      {showLeadCapture && !localStorage.getItem('leadCaptured') && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-t-3xl sm:rounded-2xl w-full max-w-md overflow-hidden animate-in slide-in-from-bottom duration-300">
            <div className="p-6">
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Gift className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-slate-800">¿Te avisamos de ofertas?</h3>
                <p className="text-slate-500 text-sm mt-1">Recibe promociones exclusivas antes que nadie</p>
              </div>
              
              <div className="space-y-3">
                <input
                  type="tel"
                  placeholder="Tu número de WhatsApp"
                  value={leadPhone}
                  onChange={(e) => setLeadPhone(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-slate-200 rounded-xl focus:border-orange-500 focus:ring-0 text-lg"
                  data-testid="lead-phone-input"
                />
                <button
                  onClick={handleLeadSubmit}
                  disabled={leadSubmitting}
                  className="w-full py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-lg transition-all disabled:opacity-50"
                  data-testid="lead-submit-btn"
                >
                  {leadSubmitting ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : '¡Sí, avísame!'}
                </button>
                <button
                  onClick={() => { setShowLeadCapture(false); localStorage.setItem('leadCaptured', 'dismissed'); }}
                  className="w-full py-2 text-slate-400 text-sm hover:text-slate-600"
                >
                  No gracias
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Floating WhatsApp Button */}
      {!showCheckout && !showCart && (businessInfo?.whatsapp || businessInfo?.phone) && (
        <a
          href={`https://wa.me/${(businessInfo?.whatsapp || businessInfo?.phone || '').replace(/\D/g, '')}?text=${encodeURIComponent('Hola! Estoy viendo su menú y tengo una pregunta')}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={recordWhatsAppClick}
          className="fixed bottom-24 left-4 z-50 bg-green-500 text-white p-4 rounded-full shadow-2xl hover:bg-green-600 hover:scale-110 transition-all animate-pulse"
          data-testid="floating-whatsapp-btn"
        >
          <MessageCircle className="w-6 h-6" />
        </a>
      )}

      {/* Sticky Header */}
      <MenuHeader 
        businessName={businessInfo?.name || "Mr. Frogs"}
        phoneNumber={businessInfo?.phone || "9811234567"}
        whatsappNumber={businessInfo?.whatsapp || businessInfo?.phone || "529811234567"}
        rightContent={
          <a
            href="/mi-tarjeta"
            className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-full text-sm font-medium transition-all hover:shadow-lg hover:shadow-amber-500/20"
          >
            <Star className="w-4 h-4" />
            <span className="hidden sm:inline">VIP</span>
          </a>
        }
      />

      {/* Floating Cart Button - Improved */}
      {cart.length > 0 && !showCheckout && (
        <button
          onClick={() => setShowCart(true)}
          className="fixed bottom-6 right-6 z-50 bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-4 rounded-full shadow-2xl hover:shadow-cyan-500/30 transition-all flex items-center gap-3 animate-bounce"
        >
          <div className="relative">
            <ShoppingCart className="w-6 h-6" />
            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
              {cartCount}
            </span>
          </div>
          <div className="text-left">
            <p className="text-sm font-medium">Ver Carrito</p>
            <p className="text-lg font-bold">{formatPrice(cartTotal)}</p>
          </div>
        </button>
      )}

      {/* Upsell Modal */}
      <UpsellModal
        isOpen={showUpsellModal}
        cart={cart}
        products={products}
        cartTotal={cartTotal}
        onAddItem={(product) => {
          addToCart(product);
          setShowUpsellModal(false);
          setShowCheckout(true);
        }}
        onSkip={() => {
          setShowUpsellModal(false);
          setShowCheckout(true);
        }}
      />

      {/* Hero Section */}
      <header className="relative overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: businessInfo?.hero_image_url 
              ? `url(${businessInfo.hero_image_url})` 
              : 'linear-gradient(135deg, #0c4a6e 0%, #164e63 50%, #134e4a 100%)'
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>

        <div className="relative z-10 px-4 py-8 md:py-16">
          {/* Top Bar */}
          <div className="max-w-6xl mx-auto flex items-center justify-between mb-8 md:mb-12">
            <div className="flex items-center gap-3">
              {businessInfo?.logo_url ? (
                <img 
                  src={businessInfo.logo_url} 
                  alt={businessInfo?.name}
                  className="w-12 h-12 md:w-16 md:h-16 rounded-full object-cover border-2 border-white/30"
                />
              ) : (
                <div className="w-12 h-12 md:w-16 md:h-16 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center text-2xl md:text-3xl">
                  🐸
                </div>
              )}
              <div>
                <h1 className="text-xl md:text-2xl font-bold text-white">{businessInfo?.name || 'Mr. Frogs'}</h1>
                <p className="text-cyan-300 text-sm">{businessInfo?.slogan || 'El mejor sabor del mar'}</p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {businessInfo?.instagram && (
                <a href={`https://instagram.com/${businessInfo.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Instagram className="w-5 h-5 text-white" />
                </a>
              )}
              {businessInfo?.facebook && (
                <a href={businessInfo.facebook.startsWith('http') ? businessInfo.facebook : `https://facebook.com/${businessInfo.facebook}`} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
                  <Facebook className="w-5 h-5 text-white" />
                </a>
              )}
            </div>
          </div>

          {/* Hero Content */}
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              {businessInfo?.description || 'Mariscos frescos que conquistan tu paladar'}
            </h2>
            <p className="text-lg md:text-xl text-white/70 mb-8">
              Pide en línea y recibe en tu domicilio o recoge en sucursal
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' })}
                className="px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                <span>Hacer Pedido</span>
              </button>
              <a
                href="/mi-tarjeta"
                className="px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all flex items-center justify-center gap-2"
              >
                <Star className="w-5 h-5" />
                <span>Mi Tarjeta VIP</span>
              </a>
            </div>
          </div>

          {/* Stats */}
          <div className="max-w-4xl mx-auto mt-12 grid grid-cols-3 gap-4">
            <div className="text-center p-4 rounded-2xl bg-white/5 backdrop-blur-sm">
              <p className="text-2xl md:text-3xl font-bold text-cyan-400">{products.length}+</p>
              <p className="text-sm text-white/60">Platillos</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-white/5 backdrop-blur-sm">
              <p className="text-2xl md:text-3xl font-bold text-green-400">30-45</p>
              <p className="text-sm text-white/60">Min. entrega</p>
            </div>
            <div className="text-center p-4 rounded-2xl bg-white/5 backdrop-blur-sm">
              <p className="text-2xl md:text-3xl font-bold text-amber-400">⭐ 4.9</p>
              <p className="text-sm text-white/60">Calificación</p>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#0f172a"/>
          </svg>
        </div>
      </header>

      {/* Promotions Carousel - NEW DYNAMIC VERSION */}
      <section className="bg-slate-950 py-8 px-4">
        <div className="max-w-6xl mx-auto">
          <PromoCarousel 
            promotions={carouselItems.length > 0 ? carouselItems.map(item => {
              const price = parseFloat(item.promo_price || item.original_price) || 0;
              const original = parseFloat(item.original_price) || 0;
              const priceText = item.is_promo && original > price
                ? `$${price.toFixed(0)} (antes $${original.toFixed(0)})`
                : price > 0 ? `$${price.toFixed(0)}` : '';
              return {
                id: `${item.type}-${item.id}`,
                title: item.title,
                subtitle: [item.description, priceText].filter(Boolean).join(' · '),
                image: item.image_url?.startsWith('/api') ? `${BACKEND_URL}${item.image_url}` : (item.image_url || 'https://images.unsplash.com/photo-1579631542720-3a87824fff86?w=800&q=80'),
                badge: item.is_promo ? 'PROMOCIÓN' : 'RECOMENDADO',
                badgeColor: item.is_promo ? 'bg-rose-500' : 'bg-amber-500',
                ctaText: 'Ver Más',
                product_id: item.product_id
              };
            }) : promotions.map(p => ({
              id: p.promotion_id,
              title: p.title,
              subtitle: p.description,
              image: p.image_url || 'https://images.unsplash.com/photo-1579631542720-3a87824fff86?w=800&q=80',
              badge: 'PROMOCIÓN',
              badgeColor: 'bg-rose-500',
              ctaText: 'Ver Más'
            }))}
            onPromoClick={(promo) => {
              const product = promo.product_id && products.find(p => p.product_id === promo.product_id);
              if (product) {
                setSelectedProduct(product);
                return;
              }
              document.getElementById('menu-section')?.scrollIntoView({ behavior: 'smooth' });
            }}
          />
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu-section" className="bg-slate-950 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header with Search */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
                Nuestro <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-400">Menú</span>
              </h2>
              <p className="text-white/60">Selecciona tus platillos favoritos</p>
            </div>
            
            {/* Search Bar */}
            <div className="w-full md:w-80">
              <SearchBar
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder="Buscar platillos..."
              />
            </div>
          </div>

          {/* Category Scroller - Netflix Style */}
          <CategoryScroller
            categories={[
              { name: 'Todos', category_id: 'all', count: products.length },
              ...visibleCategories.map(cat => ({
                ...cat,
                count: products.filter(p => p.category_id === cat.category_id).length
              }))
            ]}
            selectedCategory={selectedCategory === 'all' ? 'Todos' : getCategoryName(selectedCategory)}
            onSelectCategory={(name) => {
              if (!name || name === 'Todos') {
                setSelectedCategory('all');
              } else {
                const cat = visibleCategories.find(c => c.name === name);
                if (cat) setSelectedCategory(cat.category_id);
              }
            }}
          />

          {/* 🔥 OFERTAS DEL DÍA - All Active Promotions */}
          {allActivePromotions.length > 0 && (
            <div className="mb-8">
              <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-2xl">🔥</span> Ofertas del Día
              </h2>
              <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide">
                {allActivePromotions.map((promo) => (
                  <div
                    key={promo.id}
                    className="flex-shrink-0 w-72 snap-start bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl overflow-hidden shadow-lg cursor-pointer transform hover:scale-105 transition-all"
                    onClick={() => {
                      setActivePromotion(promo);
                      setShowPromoPopup(true);
                      fetch(`${BACKEND_URL}/api/promotions/${promo.id}/view`, { method: 'POST' }).catch(() => {});
                    }}
                  >
                    {promo.image_url && (
                      <div className="h-32 overflow-hidden">
                        <img 
                          src={promo.image_url} 
                          alt={promo.title}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <div className="p-4">
                      {promo.badge && (
                        <span className="inline-block px-2 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full mb-2">
                          {promo.badge}
                        </span>
                      )}
                      <h3 className="font-bold text-white text-lg">{promo.title}</h3>
                      {promo.subtitle && (
                        <p className="text-white/80 text-sm mt-1">{promo.subtitle}</p>
                      )}
                      <div className="flex items-center gap-2 mt-3">
                        <span className="text-2xl font-bold text-white">${promo.promo_price}</span>
                        {promo.original_price && (
                          <span className="text-white/60 line-through text-sm">${promo.original_price}</span>
                        )}
                      </div>
                      <button className="mt-3 w-full py-2 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg transition-colors">
                        ¡Lo Quiero!
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Smart Recommendations - AI Powered */}
          {products.length > 0 && (
            <SmartRecommendations
              products={products}
              cart={cart}
              customer={foundCustomer}
              purchaseHistory={purchaseHistory}
              onAddToCart={addToCart}
            />
          )}

          {/* Products Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProducts.map(product => {
              // Generate placeholder image based on category
              const getPlaceholderImage = () => {
                const category = product.category?.toLowerCase() || '';
                if (category.includes('ceviche') || category.includes('coctel')) {
                  return 'https://images.unsplash.com/photo-1611262359546-64e2822b2ab5?w=400&q=80';
                }
                if (category.includes('taco')) {
                  return 'https://images.unsplash.com/photo-1611793413292-cf250cc04aa1?w=400&q=80';
                }
                if (category.includes('bebida') || category.includes('agua') || category.includes('refresco')) {
                  return 'https://images.unsplash.com/photo-1544145945-f90425340c7e?w=400&q=80';
                }
                if (category.includes('camaron') || category.includes('camarones')) {
                  return 'https://images.unsplash.com/photo-1565680018434-b513d5e5fd47?w=400&q=80';
                }
                if (category.includes('coco')) {
                  return 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=400&q=80';
                }
                if (category.includes('filete') || category.includes('pescado')) {
                  return 'https://images.unsplash.com/photo-1519708227418-c8fd9a32b7a2?w=400&q=80';
                }
                if (category.includes('caldo') || category.includes('sopa')) {
                  return 'https://images.unsplash.com/photo-1547592166-23ac45744acd?w=400&q=80';
                }
                return 'https://images.unsplash.com/photo-1579631542720-3a87824fff86?w=400&q=80';
              };
              
              const imageUrl = product.image_url || getPlaceholderImage();
              const cartQty = getCartQuantity(product.product_id);
              
              return (
              <div
                key={product.product_id}
                className={`group relative bg-slate-900/80 backdrop-blur-xl rounded-2xl overflow-hidden border border-white/10 hover:border-cyan-400/50 transition-all duration-300 hover:shadow-xl hover:shadow-cyan-500/10 ${!product.is_available ? 'opacity-60' : ''}`}
                data-testid={`product-card-${product.product_id}`}
              >
                <div 
                  className="relative h-48 sm:h-52 overflow-hidden cursor-pointer"
                  onClick={() => setSelectedProduct(product)}
                >
                  <img 
                    src={imageUrl} 
                    alt={product.name}
                    className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 ${!product.is_available ? 'grayscale' : ''}`}
                    loading="lazy"
                  />
                  
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent opacity-60" />
                  
                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                    {product.tags?.includes('popular') && (
                      <span className="px-2 py-1 bg-amber-500/90 backdrop-blur-sm text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
                        <Star className="w-3 h-3" /> Más Vendido
                      </span>
                    )}
                    {product.tags?.includes('new') && (
                      <span className="px-2 py-1 bg-emerald-500/90 backdrop-blur-sm text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
                        <Sparkles className="w-3 h-3" /> Nuevo
                      </span>
                    )}
                    {product.tags?.includes('spicy') && (
                      <span className="px-2 py-1 bg-rose-500/90 backdrop-blur-sm text-white text-xs font-bold rounded-full flex items-center gap-1 shadow-lg">
                        <Flame className="w-3 h-3" /> Picante
                      </span>
                    )}
                  </div>

                  {/* Quick view button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedProduct(product);
                    }}
                    className="absolute top-3 right-3 bg-black/40 hover:bg-black/60 backdrop-blur-sm text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                  >
                    <Eye className="w-4 h-4" />
                  </button>

                  {/* Out of stock overlay */}
                  {!product.is_available && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="bg-slate-800 text-white px-4 py-2 rounded-full text-sm font-bold">
                        Agotado
                      </span>
                    </div>
                  )}

                  {/* Cart quantity badge */}
                  {cartQty > 0 && (
                    <div className="absolute bottom-3 right-3 bg-cyan-500 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm shadow-lg animate-bounce">
                      {cartQty}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <p className="text-xs text-cyan-400 font-medium uppercase tracking-wider mb-1">
                    {product.category}
                  </p>
                  
                  <h3 className="text-lg font-bold text-white mb-1 line-clamp-2 leading-tight">
                    {product.name}
                  </h3>
                  
                  {product.description && (
                    <p className="text-sm text-slate-400 line-clamp-2 mb-3">
                      {product.description.replace(/\*\*/g, '').substring(0, 60)}...
                    </p>
                  )}

                  {/* Price and Add Button */}
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-black text-cyan-400">
                      ${product.price?.toFixed(0) || '0'}
                    </span>
                    
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (product.is_available) {
                          addToCart(product);
                          toast.success(`${product.name} agregado`, { duration: 1500 });
                        }
                      }}
                      disabled={!product.is_available}
                      className={`flex items-center gap-2 px-4 py-2.5 rounded-full font-bold text-sm transition-all duration-300 active:scale-90 ${
                        !product.is_available 
                          ? 'bg-slate-700 text-slate-500 cursor-not-allowed'
                          : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-lg shadow-cyan-500/20'
                      }`}
                      data-testid={`add-to-cart-btn-${product.product_id}`}
                    >
                      <Plus className="w-4 h-4" />
                      <span>Agregar</span>
                    </button>
                  </div>
                </div>
              </div>
            );})}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <div className="text-6xl mb-4 opacity-30">🍽️</div>
              <p className="text-white/60">
                {searchQuery ? `No hay resultados para "${searchQuery}"` : 'No hay productos en esta categoría'}
              </p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-4 px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-full text-sm hover:bg-cyan-500/30 transition-colors"
                >
                  Limpiar búsqueda
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* VIP Section */}
      <section className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 py-16 px-4 border-y border-amber-500/20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/20 rounded-full mb-6">
            <Award className="w-5 h-5 text-amber-400" />
            <span className="text-amber-400 font-semibold">Club VIP</span>
          </div>
          
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Gana <span className="text-amber-400">puntos</span> con cada pedido
          </h2>
          <p className="text-white/70 mb-8 max-w-2xl mx-auto">
            Por cada $10 que gastes, ganas 1 punto. ¡Canjéalos por productos gratis!
          </p>
          
          <a
            href="/mi-tarjeta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-bold rounded-full hover:shadow-lg hover:shadow-amber-500/30 transition-all"
          >
            <Award className="w-5 h-5" />
            Ver mi Tarjeta VIP
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-slate-900 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">Encuéntranos</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {businessInfo?.address && (
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Dirección</h3>
                  <p className="text-white/70 text-sm">{businessInfo.address}</p>
                </div>
              </div>
            )}

            {businessInfo?.phone && (
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Teléfono</h3>
                  <p className="text-white/70 text-sm">{businessInfo.phone}</p>
                </div>
              </div>
            )}

            {businessInfo?.schedule && (
              <div className="flex items-start gap-4 p-4 rounded-2xl bg-white/5">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white mb-1">Horario</h3>
                  <p className="text-white/70 text-sm">{businessInfo.schedule}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-8 px-4 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="text-2xl">🐸</span>
            <span className="text-white font-bold">{businessInfo?.name || 'Mr. Frogs'}</span>
          </div>
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} {businessInfo?.name || 'Mr. Frogs'}. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* ========== CART MODAL ========== */}
      {showCart && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-slate-900 w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl max-h-[90vh] overflow-hidden animate-slide-up">
            {/* Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-500 p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <ShoppingCart className="w-6 h-6 text-white" />
                <h2 className="text-xl font-bold text-white">Tu Pedido</h2>
              </div>
              <button onClick={() => setShowCart(false)} className="text-white/80 hover:text-white p-2">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Cart Items */}
            <div className="p-4 max-h-[50vh] overflow-y-auto">
              {cart.length === 0 ? (
                <div className="text-center py-8">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-white/20" />
                  <p className="text-white/60">Tu carrito está vacío</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cart.map((item, index) => (
                    <div key={`${item.product_id}-${index}`} className="bg-slate-800 rounded-xl p-3">
                      <div className="flex items-center gap-3">
                        {item.image_url ? (
                          <img src={item.image_url} alt={item.name} className="w-16 h-16 rounded-lg object-cover" />
                        ) : (
                          <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center text-2xl">🍽️</div>
                        )}
                        <div className="flex-1">
                          <h4 className="font-semibold text-white text-sm">{item.name}</h4>
                          <p className="text-cyan-400 font-bold">{formatPrice(item.price * item.quantity)}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(index, -1)}
                            className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white hover:bg-slate-600"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                          <span className="text-white font-bold w-6 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(index, 1)}
                            className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white hover:bg-slate-600"
                          >
                            <Plus className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => removeFromCart(index)}
                            className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 hover:bg-red-500/30 ml-2"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      {/* Notes input for each item */}
                      <div className="mt-2">
                        <input
                          type="text"
                          value={item.notes || ''}
                          onChange={(e) => updateItemNotes(index, e.target.value)}
                          placeholder="Ej: Sin cebolla, extra limón..."
                          className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-xs placeholder:text-white/40 focus:outline-none focus:border-cyan-500"
                        />
                        {item.notes && (
                          <p className="text-orange-400 text-xs mt-1">📝 {item.notes}</p>
                        )}
                      </div>
                      {/* Extras seleccionados */}
                      {item.selectedExtras && item.selectedExtras.length > 0 && (
                        <div className="mt-2 p-2 bg-cyan-500/10 rounded-lg border border-cyan-500/20">
                          <p className="text-cyan-400 text-xs font-medium mb-1">🧂 Extras:</p>
                          <div className="flex flex-wrap gap-1">
                            {item.selectedExtras.map((extra, idx) => (
                              <span key={idx} className="text-xs bg-cyan-500/20 text-cyan-300 px-2 py-0.5 rounded">
                                {extra.name} +{formatPrice(extra.price)}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {cart.length > 0 && (
              <div className="p-4 border-t border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-white/70">Subtotal</span>
                  <span className="text-2xl font-bold text-white">{formatPrice(cartTotal)}</span>
                </div>
                <button
                  onClick={() => { 
                    setShowCart(false); 
                    // Show upsell modal first if cart is under $300
                    if (cartTotal < 300 && products.length > cart.length) {
                      setShowUpsellModal(true);
                    } else {
                      setShowCheckout(true);
                    }
                  }}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-full hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  Continuar con el Pedido
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== CHECKOUT MODAL ========== */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-slate-900 w-full max-w-lg rounded-3xl max-h-[95vh] overflow-y-auto my-4">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-r from-cyan-500 to-blue-500 p-4 flex items-center justify-between z-10">
              <div className="flex items-center gap-3">
                {checkoutStep > 1 && checkoutStep < 4 && (
                  <button onClick={() => setCheckoutStep(s => s - 1)} className="text-white/80 hover:text-white">
                    <ArrowLeft className="w-6 h-6" />
                  </button>
                )}
                <h2 className="text-xl font-bold text-white">
                  {checkoutStep === 1 && '¿Quién eres?'}
                  {checkoutStep === 2 && '¿Dónde lo entregamos?'}
                  {checkoutStep === 3 && 'Confirmar Pedido'}
                  {checkoutStep === 4 && '¡Pedido Enviado!'}
                </h2>
              </div>
              <button onClick={resetCheckout} className="text-white/80 hover:text-white p-2">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Progress Steps */}
            {checkoutStep < 4 && (
              <div className="px-4 py-3 bg-slate-800/50">
                <div className="flex items-center justify-between">
                  {[1, 2, 3].map(step => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        checkoutStep >= step ? 'bg-cyan-500 text-white' : 'bg-slate-700 text-white/50'
                      }`}>
                        {checkoutStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                      </div>
                      {step < 3 && (
                        <div className={`w-12 sm:w-24 h-1 mx-1 rounded ${
                          checkoutStep > step ? 'bg-cyan-500' : 'bg-slate-700'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Customer Info */}
            {checkoutStep === 1 && (
              <div className="p-4 space-y-4">
                <div className="bg-slate-800/50 rounded-2xl p-4">
                  <label className="block text-white/70 text-sm mb-2">📱 Tu número de teléfono</label>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      value={customerForm.phone}
                      onChange={(e) => setCustomerForm({ ...customerForm, phone: e.target.value })}
                      placeholder="622 123 4567"
                      className="flex-1 bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                    <button
                      onClick={searchCustomer}
                      disabled={searchingCustomer}
                      className="px-4 py-3 bg-cyan-500 text-white rounded-xl hover:bg-cyan-600 disabled:opacity-50"
                    >
                      {searchingCustomer ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* Found Customer */}
                {foundCustomer && (
                  <div className="space-y-3">
                    <div className="bg-green-500/10 border border-green-500/30 rounded-2xl p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <p className="text-white font-bold">¡Bienvenido de vuelta!</p>
                          <p className="text-green-400">{foundCustomer.name}</p>
                        </div>
                      </div>
                    </div>
                    
                    {/* Loyalty Progress Bar */}
                    {foundCustomer.points_available >= 0 && (
                      <LoyaltyProgress 
                        customer={{ 
                          ...foundCustomer, 
                          points: foundCustomer.points_available 
                        }} 
                        nextReward={100}
                      />
                    )}
                  </div>
                )}

                {/* New Customer Form */}
                {(isNewCustomer || !foundCustomer) && customerForm.phone.length >= 10 && (
                  <div className="bg-slate-800/50 rounded-2xl p-4 space-y-3">
                    <p className="text-white/70 text-sm">👤 Ingresa tus datos</p>
                    <input
                      type="text"
                      value={customerForm.name}
                      onChange={(e) => setCustomerForm({ ...customerForm, name: e.target.value })}
                      placeholder="Tu nombre completo"
                      className="w-full bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    />
                  </div>
                )}

                {(foundCustomer || customerForm.name) && (
                  <button
                    onClick={() => setCheckoutStep(2)}
                    disabled={!customerForm.name}
                    className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    Continuar
                    <ArrowRight className="w-5 h-5" />
                  </button>
                )}
              </div>
            )}

            {/* Step 2: Delivery Info */}
            {checkoutStep === 2 && (
              <div className="p-4 space-y-4">
                {/* Order Type */}
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setCustomerForm({ ...customerForm, orderType: 'delivery' })}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      customerForm.orderType === 'delivery'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-700 bg-slate-800/50'
                    }`}
                  >
                    <Home className={`w-8 h-8 ${customerForm.orderType === 'delivery' ? 'text-cyan-400' : 'text-white/50'}`} />
                    <span className={`font-semibold ${customerForm.orderType === 'delivery' ? 'text-cyan-400' : 'text-white/70'}`}>
                      Domicilio
                    </span>
                  </button>
                  <button
                    onClick={() => setCustomerForm({ ...customerForm, orderType: 'pickup' })}
                    className={`p-4 rounded-2xl border-2 transition-all flex flex-col items-center gap-2 ${
                      customerForm.orderType === 'pickup'
                        ? 'border-cyan-500 bg-cyan-500/10'
                        : 'border-slate-700 bg-slate-800/50'
                    }`}
                  >
                    <Store className={`w-8 h-8 ${customerForm.orderType === 'pickup' ? 'text-cyan-400' : 'text-white/50'}`} />
                    <span className={`font-semibold ${customerForm.orderType === 'pickup' ? 'text-cyan-400' : 'text-white/70'}`}>
                      Recoger
                    </span>
                  </button>
                </div>

                {/* Delivery Address */}
                {customerForm.orderType === 'delivery' && (
                  <div className="space-y-3">
                    <div className="bg-slate-800/50 rounded-2xl p-4 space-y-3">
                      <div className="flex items-center gap-2 text-white/70">
                        <MapPinned className="w-5 h-5" />
                        <span className="text-sm font-medium">Dirección de entrega</span>
                      </div>
                      <input
                        type="text"
                        value={customerForm.address}
                        onChange={(e) => setCustomerForm({ ...customerForm, address: e.target.value })}
                        placeholder="Calle y número"
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <input
                        type="text"
                        value={customerForm.colony}
                        onChange={(e) => setCustomerForm({ ...customerForm, colony: e.target.value })}
                        placeholder="Colonia"
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                      <input
                        type="text"
                        value={customerForm.references}
                        onChange={(e) => setCustomerForm({ ...customerForm, references: e.target.value })}
                        placeholder="Referencias (casa blanca, portón negro...)"
                        className="w-full bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500"
                      />
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="bg-slate-800/50 rounded-2xl p-4">
                  <div className="flex items-center gap-2 text-white/70 mb-2">
                    <FileText className="w-5 h-5" />
                    <span className="text-sm font-medium">Notas especiales (opcional)</span>
                  </div>
                  <textarea
                    value={customerForm.notes}
                    onChange={(e) => setCustomerForm({ ...customerForm, notes: e.target.value })}
                    placeholder="Sin cebolla, extra limón, etc..."
                    rows={2}
                    className="w-full bg-slate-700 text-white px-4 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-cyan-500 resize-none"
                  />
                </div>

                <button
                  onClick={() => setCheckoutStep(3)}
                  disabled={customerForm.orderType === 'delivery' && !customerForm.address}
                  className="w-full py-4 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-bold rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  Revisar Pedido
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Step 3: Confirmation */}
            {checkoutStep === 3 && (
              <div className="p-4 space-y-4">
                {/* Customer Summary */}
                <div className="bg-slate-800/50 rounded-2xl p-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-cyan-400" />
                    Datos del Cliente
                  </h3>
                  <div className="space-y-1 text-sm">
                    <p className="text-white">{customerForm.name}</p>
                    <p className="text-white/70">{customerForm.phone}</p>
                    {customerForm.orderType === 'delivery' && (
                      <>
                        <p className="text-white/70 mt-2">{customerForm.address}</p>
                        {customerForm.colony && <p className="text-white/70">{customerForm.colony}</p>}
                        {customerForm.references && <p className="text-white/50 text-xs">{customerForm.references}</p>}
                      </>
                    )}
                  </div>
                  <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/20">
                    {customerForm.orderType === 'delivery' ? (
                      <><Home className="w-4 h-4 text-cyan-400" /><span className="text-cyan-400 text-sm">Domicilio</span></>
                    ) : (
                      <><Store className="w-4 h-4 text-cyan-400" /><span className="text-cyan-400 text-sm">Recoger en sucursal</span></>
                    )}
                  </div>
                </div>

                {/* Order Summary */}
                <div className="bg-slate-800/50 rounded-2xl p-4">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5 text-cyan-400" />
                    Tu Pedido
                  </h3>
                  <div className="space-y-2">
                    {cart.map((item, index) => (
                      <div key={`${item.product_id}-${index}`} className="text-sm">
                        <div className="flex justify-between">
                          <span className="text-white/70">{item.quantity}x {item.name}</span>
                          <span className="text-white">{formatPrice(item.price * item.quantity)}</span>
                        </div>
                        {item.notes && (
                          <p className="text-orange-400 text-xs mt-0.5 ml-4">📝 {item.notes}</p>
                        )}
                      </div>
                    ))}
                  </div>
                  {customerForm.notes && (
                    <p className="mt-3 text-xs text-white/50 italic">📝 Nota general: {customerForm.notes}</p>
                  )}
                </div>

                {/* Total */}
                <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-2xl p-4 border border-cyan-500/30">
                  <div className="flex justify-between items-center">
                    <span className="text-white/70">Subtotal</span>
                    <span className="text-white font-semibold">{formatPrice(cartTotal)}</span>
                  </div>
                  <div className="flex justify-between items-center mt-1">
                    <span className="text-amber-400 flex items-center gap-1">
                      <Gift className="w-4 h-4" /> Puntos a ganar
                    </span>
                    <span className="text-amber-400 font-semibold">+{Math.floor(cartTotal * 0.10)} pts</span>
                  </div>
                  <div className="border-t border-white/10 mt-3 pt-3 flex justify-between items-center">
                    <span className="text-white font-bold text-lg">Total</span>
                    <span className="text-2xl font-bold text-cyan-400">{formatPrice(cartTotal)}</span>
                  </div>
                </div>

                <button
                  onClick={submitOrder}
                  disabled={submittingOrder}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-bold rounded-full disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {submittingOrder ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Enviando...</>
                  ) : (
                    <><CheckCircle className="w-5 h-5" /> Confirmar Pedido</>
                  )}
                </button>
              </div>
            )}

            {/* Step 4: Success */}
            {checkoutStep === 4 && orderResult && (
              <div className="p-6 text-center">
                <div className="w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full flex items-center justify-center animate-bounce">
                  <PartyPopper className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-2xl font-bold text-white mb-2">¡Pedido Confirmado!</h2>
                <p className="text-white/70 mb-6">Tu pedido ha sido enviado a cocina</p>
                
                <div className="bg-slate-800/50 rounded-2xl p-4 mb-6">
                  <p className="text-white/50 text-sm">Número de Pedido</p>
                  <p className="text-3xl font-bold text-cyan-400">#{orderResult.order.order_number}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-800/50 rounded-xl p-3">
                    <p className="text-white/50 text-xs">Total</p>
                    <p className="text-xl font-bold text-white">{formatPrice(orderResult.order.total)}</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl p-3">
                    <p className="text-white/50 text-xs">Tiempo estimado</p>
                    <p className="text-xl font-bold text-white">{orderResult.order.estimated_time}</p>
                  </div>
                </div>
                
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 mb-6">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    <span className="text-amber-400 font-semibold">
                      ¡Ganaste {orderResult.order.points_earned} puntos!
                    </span>
                  </div>
                </div>

                {/* WhatsApp Notification Button */}
                {(businessInfo?.whatsapp || businessInfo?.phone) && (
                  <a
                    href={(() => {
                      const whatsappNumber = (businessInfo?.whatsapp || businessInfo?.phone || '').replace(/\D/g, '');
                      // Usar los items de la orden creada, no del carrito
                      const orderItems = orderResult?.order?.items || cart;
                      const itemsList = orderItems.map(item => {
                        const itemName = item.product_name || item.name;
                        const itemQty = item.quantity;
                        const itemPrice = item.subtotal || (item.price * item.quantity);
                        const itemDesc = item.description;
                        const itemNotes = item.notes;
                        
                        let itemText = `• ${itemQty}x ${itemName} - ${formatPrice(itemPrice)}`;
                        if (itemDesc) {
                          itemText += `\n   📋 ${itemDesc}`;
                        }
                        if (itemNotes) {
                          itemText += `\n   📝 Nota: ${itemNotes}`;
                        }
                        return itemText;
                      }).join('\n');
                      const orderType = customerForm.orderType === 'delivery' ? '🏠 Domicilio' : '🏪 Recoger';
                      const addressText = customerForm.orderType === 'delivery'
                        ? `\n📍 ${customerForm.address}, ${customerForm.colony}${customerForm.references ? `\n🔍 Ref: ${customerForm.references}` : ''}`
                        : '';
                      const message = `🍽️ *NUEVO PEDIDO*\n` +
                        `${businessInfo?.name || 'Mr. Frogs'}\n\n` +
                        `📋 Pedido #${orderResult.order.order_number}\n` +
                        `👤 ${customerForm.name}\n` +
                        `📱 ${customerForm.phone}\n` +
                        `🚗 ${orderType}${addressText}\n\n` +
                        `*PRODUCTOS:*\n${itemsList}\n\n` +
                        `💰 *TOTAL: ${formatPrice(orderResult.order.total)}*\n` +
                        `${customerForm.notes ? `\n📝 Notas: ${customerForm.notes}\n` : ''}` +
                        `\n_Menú Digital_`;
                      return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(message)}`;
                    })()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full py-3 mb-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-semibold rounded-full hover:from-green-600 hover:to-green-700 transition-all flex items-center justify-center gap-2"
                    data-testid="whatsapp-notification-btn"
                  >
                    <MessageCircle className="w-5 h-5" />
                    Confirmar por WhatsApp
                  </a>
                )}
                
                <div className="space-y-3">
                  <a
                    href="/mi-tarjeta"
                    className="block w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-full"
                  >
                    Ver mi Tarjeta VIP
                  </a>
                  <button
                    onClick={resetCheckout}
                    className="block w-full py-3 bg-slate-700 text-white font-semibold rounded-full hover:bg-slate-600"
                  >
                    Hacer otro pedido
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>

      {/* Product Detail Modal */}
      {selectedProduct && (
        <div 
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelectedProduct(null)}
        >
          <div 
            className="bg-slate-900 rounded-3xl overflow-hidden max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Product Image */}
            <div className="relative h-64 sm:h-72">
              {selectedProduct.image_url ? (
                <img 
                  src={selectedProduct.image_url} 
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-slate-700 to-slate-800 flex items-center justify-center">
                  <span className="text-8xl opacity-30">🍽️</span>
                </div>
              )}
              
              {/* Close button */}
              <button
                onClick={() => setSelectedProduct(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              
              {/* Tags */}
              <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                {selectedProduct.tags?.includes('popular') && (
                  <span className="px-3 py-1.5 bg-amber-500 text-white text-sm font-bold rounded-full flex items-center gap-1">
                    <Star className="w-4 h-4" /> Popular
                  </span>
                )}
                {selectedProduct.tags?.includes('new') && (
                  <span className="px-3 py-1.5 bg-green-500 text-white text-sm font-bold rounded-full flex items-center gap-1">
                    <Sparkles className="w-4 h-4" /> Nuevo
                  </span>
                )}
                {selectedProduct.tags?.includes('spicy') && (
                  <span className="px-3 py-1.5 bg-red-500 text-white text-sm font-bold rounded-full flex items-center gap-1">
                    <Flame className="w-4 h-4" /> Picante
                  </span>
                )}
              </div>
            </div>
            
            {/* Product Info */}
            <div className="p-6">
              {/* Category */}
              <p className="text-cyan-400 text-sm font-medium mb-2">
                {getCategoryName(selectedProduct.category_id)}
              </p>
              
              {/* Name */}
              <h2 className="text-2xl font-bold text-white mb-3">
                {selectedProduct.name}
              </h2>
              
              {/* Description - Full text */}
              {selectedProduct.description && (
                <div className="mb-4">
                  <p className="text-white/70 leading-relaxed" dangerouslySetInnerHTML={{ __html: parseMarkdown(selectedProduct.description) }} />
                </div>
              )}
              
              {/* Extras Section */}
              {productExtras.length > 0 && (
                <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/10">
                  <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                    🧂 Extras disponibles
                  </h3>
                  <div className="space-y-2">
                    {productExtras.map((extra) => (
                      <label
                        key={extra.extra_id}
                        className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all ${
                          selectedExtras.some(e => e.extra_id === extra.extra_id)
                            ? 'bg-cyan-500/20 border border-cyan-500/50'
                            : 'bg-white/5 border border-white/10 hover:bg-white/10'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedExtras.some(e => e.extra_id === extra.extra_id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedExtras([...selectedExtras, extra]);
                              } else {
                                setSelectedExtras(selectedExtras.filter(ex => ex.extra_id !== extra.extra_id));
                              }
                            }}
                            className="w-5 h-5 rounded border-white/30 bg-white/10 text-cyan-500 focus:ring-cyan-500"
                          />
                          <span className="text-white font-medium">{extra.name}</span>
                        </div>
                        <span className="text-cyan-400 font-bold">+{formatPrice(extra.price)}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Price and Add to Cart */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <div>
                  <p className="text-sm text-white/50">
                    {selectedExtras.length > 0 ? 'Total' : 'Precio'}
                  </p>
                  <span className="text-3xl font-bold text-cyan-400">
                    {formatPrice(calculateTotalWithExtras())}
                  </span>
                  {selectedExtras.length > 0 && (
                    <p className="text-xs text-white/40">
                      Base: {formatPrice(selectedProduct.price)} + Extras: {formatPrice(selectedExtras.reduce((s, e) => s + e.price, 0))}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    // Agregar producto con extras al carrito
                    const productWithExtras = {
                      ...selectedProduct,
                      selectedExtras: selectedExtras,
                      extrasTotal: selectedExtras.reduce((sum, e) => sum + e.price, 0),
                      totalPrice: calculateTotalWithExtras()
                    };
                    addToCart(productWithExtras);
                    setSelectedProduct(null);
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-cyan-500/30 transition-all flex items-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Agregar al carrito
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Install App Prompt */}
      {showInstallPrompt && (
        <InstallAppPrompt onClose={handleInstallPromptClose} />
      )}

      {/* Froggy ELITE ANIMATED - AI Sales Assistant */}
      <FroggyEliteAnimated
        customerName={foundCustomer?.name || customerForm.name}
        customerPhone={foundCustomer?.phone || customerForm.phone}
        currentProduct={null}
        cartTotal={cartTotal}
        cartItems={cart.map(item => item.name)}
        onAddToCart={(productName) => {
          const product = products.find(p => p.name?.toLowerCase().includes(productName.toLowerCase()));
          if (product) {
            addToCart(product);
            toast.success(`🐸 ¡${product.name} agregado al carrito!`);
          }
        }}
      />
    </div>
  );
};

export default MenuPublic;
